"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { BG_THEMES, type BgTheme } from "@/lib/data/backgrounds";
import { useLocalStorageState } from "@/lib/hooks/useLocalStorageState";

interface PersonalizationContextValue {
  /** Index into BG_THEMES for the active background. */
  bgIndex: number;
  setBgIndex: (index: number) => void;
  themes: BgTheme[];
  activeTheme: BgTheme;
  /** False until the stored preference has been read from localStorage. */
  hydrated: boolean;
}

const PersonalizationContext = createContext<PersonalizationContextValue | null>(null);

export function PersonalizationProvider({ children }: { children: ReactNode }) {
  // Persisted to localStorage — this is the intended production behavior.
  const [bgIndex, setBgIndex, hydrated] = useLocalStorageState<number>("psycloud_bg", 0);

  const value = useMemo<PersonalizationContextValue>(
    () => ({
      bgIndex,
      setBgIndex,
      themes: BG_THEMES,
      activeTheme: BG_THEMES[bgIndex] ?? BG_THEMES[0],
      hydrated,
    }),
    [bgIndex, setBgIndex, hydrated],
  );

  return (
    <PersonalizationContext.Provider value={value}>
      {children}
    </PersonalizationContext.Provider>
  );
}

export function usePersonalization(): PersonalizationContextValue {
  const ctx = useContext(PersonalizationContext);
  if (!ctx)
    throw new Error(
      "usePersonalization must be used within <PersonalizationProvider>",
    );
  return ctx;
}
